/**
 * Question card component.
 *
 * Displays individual question with answer options.
 *
 * @module QuestionCard
 */

import { Eye, EyeOff, Flag, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AdaptiveQuestionStateBadge } from "./AdaptiveQuestionStateBadge";
import type { McqFocusBindingProps } from "@/hooks/useMcqKeyboardNavigation";
import type { Question, AnswerOption } from "@/types/exam-simulation";
import { cn } from "@/lib/utils";

interface QuestionCardProps {
  readonly question: Question;
  readonly isActive: boolean;
  readonly isFlagged: boolean;
  readonly selectedAnswer: string | null;
  readonly struckAnswers: string[];
  readonly isSubmitted?: boolean;
  readonly evaluation?: {
    readonly selectedIsCorrect?: boolean | null;
    readonly expectedAnswerId?: string | null;
  } | null;
  readonly onSelect: () => void;
  readonly onAnswerSelect: (answerId: string) => void;
  readonly onStrikeToggle: (answerId: string) => void;
  readonly onFlagToggle: () => void;
  readonly questionFocusProps?: McqFocusBindingProps | null;
  readonly getOptionFocusProps?: (
    optionId: string,
  ) => McqFocusBindingProps | null;
  readonly isQuestionKeyboardFocused?: boolean;
  readonly isOptionKeyboardFocused?: (optionId: string) => boolean;
}

export function QuestionCard({
  question,
  isActive,
  isFlagged,
  selectedAnswer,
  struckAnswers,
  isSubmitted = false,
  evaluation = null,
  onSelect,
  onAnswerSelect,
  onStrikeToggle,
  onFlagToggle,
  questionFocusProps = null,
  getOptionFocusProps,
  isQuestionKeyboardFocused = false,
  isOptionKeyboardFocused,
}: QuestionCardProps) {
  const { t } = useTranslation();
  const stemId = `question-stem-${question.id}`;
  const showQuestionFocusRing = isActive || isQuestionKeyboardFocused;
  const isMatchingQuestion = question.type === "matching";

  return (
    <div
      className={cn(
        "w-full rounded-xl border bg-card p-3 text-left transition-all duration-300 sm:p-5",
        showQuestionFocusRing
          ? "border-primary/40 ring-2 ring-primary/20"
          : "border-border hover:border-primary/20 hover:shadow-md",
        isFlagged && "bg-exam-flag/5",
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
        <button
          type="button"
          aria-labelledby={stemId}
          className="flex-1 min-w-0 rounded-lg text-left cursor-pointer focus-visible:outline-none"
          onClick={onSelect}
          {...(questionFocusProps ?? { tabIndex: -1 })}
        >
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <span
              className={cn(
                "w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs sm:text-sm font-bold transition-all duration-300 shadow-sm font-ui",
                isActive
                  ? "bg-primary text-primary-foreground scale-110"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {question.id}
            </span>
            {question.adaptiveState && (
              <AdaptiveQuestionStateBadge state={question.adaptiveState} />
            )}
            {isFlagged && (
              <Badge
                variant="secondary"
                className="bg-exam-flag/10 text-exam-flag border-none text-[10px] px-1.5 h-5 flex sm:hidden font-ui"
              >
                Flagged
              </Badge>
            )}
          </div>

          <p
            id={stemId}
            className="font-ui font-bold text-foreground text-sm sm:text-base leading-relaxed tracking-tight underline-offset-4 decoration-primary/20 whitespace-pre-line"
          >
            {question.stem}
          </p>
        </button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          tabIndex={-1}
          className={cn(
            "h-11 w-11 shrink-0 rounded-full p-0 transition-all lg:h-8 lg:w-8",
            isFlagged
              ? "bg-exam-flag/10 text-exam-flag hover:bg-exam-flag/20"
              : "hover:bg-muted text-muted-foreground",
          )}
          onClick={onFlagToggle}
        >
          <Flag
            className={cn(
              "w-3.5 h-3.5 sm:w-4 sm:h-4",
              isFlagged && "fill-exam-flag",
            )}
          />
        </Button>
      </div>

      {isMatchingQuestion ? (
        <MatchingAnswerSelect
          stemId={stemId}
          selectedAnswer={selectedAnswer}
          options={question.options}
          isSubmitted={isSubmitted}
          placeholder={t("simulation.active.matchingSelectPlaceholder", {
            defaultValue: "Select answer",
          })}
          onAnswerSelect={onAnswerSelect}
        />
      ) : (
        <div className="space-y-2" role="radiogroup" aria-labelledby={stemId}>
          {question.options.map((option) => (
            <AnswerOptionItem
              key={option.id}
              option={option}
              isSelected={selectedAnswer === option.id}
              isStruck={struckAnswers.includes(option.id)}
              isSubmitted={isSubmitted}
              isKeyboardFocused={isOptionKeyboardFocused?.(option.id) ?? false}
              focusProps={getOptionFocusProps?.(option.id) ?? null}
              isExpectedAnswer={Boolean(
                isSubmitted &&
                  evaluation?.expectedAnswerId &&
                  evaluation.expectedAnswerId === option.id,
              )}
              isIncorrectSelected={Boolean(
                isSubmitted &&
                  selectedAnswer === option.id &&
                  evaluation?.selectedIsCorrect === false,
              )}
              onSelect={() => onAnswerSelect(option.id)}
              onStrike={() => onStrikeToggle(option.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface MatchingAnswerSelectProps {
  readonly stemId: string;
  readonly selectedAnswer: string | null;
  readonly options: AnswerOption[];
  readonly isSubmitted: boolean;
  readonly placeholder: string;
  readonly onAnswerSelect: (answerId: string) => void;
}

function formatMatchingOptionLabel(option: AnswerOption): string {
  return `${option.id.toUpperCase()}. ${option.text}`;
}

function MatchingAnswerSelect({
  stemId,
  selectedAnswer,
  options,
  isSubmitted,
  placeholder,
  onAnswerSelect,
}: MatchingAnswerSelectProps) {
  return (
    <div className="space-y-2">
      <Select
        value={selectedAnswer ?? undefined}
        onValueChange={onAnswerSelect}
        disabled={isSubmitted}
      >
        <SelectTrigger
          aria-labelledby={stemId}
          className={cn(
            "h-11 w-full bg-background border-border font-ui text-sm transition-all",
            "hover:border-primary/30 focus:ring-primary/20",
            selectedAnswer &&
              "border-primary/40 bg-primary/[0.04] text-foreground",
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.id}
              value={option.id}
              className="font-ui text-sm"
            >
              {formatMatchingOptionLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface AnswerOptionItemProps {
  readonly option: AnswerOption;
  readonly isSelected: boolean;
  readonly isStruck: boolean;
  readonly isSubmitted: boolean;
  readonly isExpectedAnswer: boolean;
  readonly isIncorrectSelected: boolean;
  readonly isKeyboardFocused: boolean;
  readonly focusProps: McqFocusBindingProps | null;
  readonly onSelect: () => void;
  readonly onStrike: () => void;
}

function AnswerOptionItem({
  option,
  isSelected,
  isStruck,
  isSubmitted,
  isExpectedAnswer,
  isIncorrectSelected,
  isKeyboardFocused,
  focusProps,
  onSelect,
  onStrike,
}: AnswerOptionItemProps) {
  const isEvaluationCorrect = isSubmitted && isExpectedAnswer;
  const isEvaluationIncorrect = isSubmitted && isIncorrectSelected;
  const strikeActionLabel = isStruck ? "Restore option" : "Eliminate option";
  const optionLabelToneClass = (() => {
    if (isEvaluationCorrect) {
      return "text-emerald-700 dark:text-emerald-400";
    }
    if (isEvaluationIncorrect) {
      return "text-amber-700 dark:text-amber-400";
    }
    if (isSelected && !isStruck) {
      return "text-primary";
    }
    return "text-muted-foreground";
  })();

  return (
    <div
      className={cn(
        "group flex min-h-11 items-center gap-2 rounded-lg border p-2.5 transition-all duration-200 sm:gap-3 sm:p-3",
        (() => {
          if (isStruck) return "bg-muted/30 border-transparent opacity-60";
          if (isEvaluationCorrect) {
            return "bg-emerald-50 border-emerald-400 ring-1 ring-emerald-100 dark:bg-emerald-950/20 dark:border-emerald-700";
          }
          if (isEvaluationIncorrect) {
            return "bg-amber-50 border-amber-400 ring-1 ring-amber-100 dark:bg-amber-950/20 dark:border-amber-700";
          }
          if (isSelected) {
            return "bg-primary/[0.04] border-primary/40 shadow-sm ring-1 ring-primary/10";
          }
          return "bg-background border-border hover:border-primary/30 hover:bg-accent/30";
        })(),
        isKeyboardFocused && !isStruck && "ring-2 ring-primary/50",
      )}
    >
      <button
        type="button"
        role="radio"
        aria-checked={isSelected}
        disabled={isStruck}
        className={cn(
          "flex flex-1 min-w-0 items-center gap-3 text-left rounded-md focus-visible:outline-none cursor-pointer disabled:cursor-not-allowed",
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (!isStruck) {
            onSelect();
          }
        }}
        {...(focusProps ?? { tabIndex: -1 })}
      >
        <span
          className={cn(
            "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all shadow-sm",
            (() => {
              if (isStruck) return "border-muted-foreground/30 bg-transparent";
              if (isEvaluationCorrect)
                return "border-emerald-600 bg-emerald-600";
              if (isEvaluationIncorrect) return "border-amber-500 bg-amber-500";
              if (isSelected) return "border-primary bg-primary/10 scale-110";
              return "border-muted-foreground/40 group-hover:border-primary bg-background";
            })(),
          )}
          aria-hidden
        >
          {isSelected && !isStruck && (
            <>
              {isSubmitted ? (
                <Check className="w-3 h-3 text-white stroke-[3px]" />
              ) : (
                <span className="h-2 w-2 rounded-full bg-primary" />
              )}
            </>
          )}
        </span>

        <span
          className={cn(
            "inline-flex items-center gap-1.5 sm:gap-2 text-[13px] sm:text-sm leading-relaxed font-ui",
            isStruck
              ? "line-through text-muted-foreground/60"
              : "text-foreground font-medium",
          )}
        >
          {!["true", "false"].includes(option.id.toLowerCase()) && (
            <span
              className={cn(
                "font-bold uppercase shrink-0",
                optionLabelToneClass,
              )}
            >
              {option.id}.
            </span>
          )}
          <span className="break-words">{option.text}</span>
        </span>
      </button>

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            tabIndex={-1}
            aria-label={strikeActionLabel}
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all lg:h-8 lg:w-8",
              isStruck
                ? "bg-muted-foreground/10 text-muted-foreground"
                : "bg-muted/60 text-muted-foreground hover:bg-muted",
            )}
            onClick={(e) => {
              e.stopPropagation();
              onStrike();
            }}
          >
            {isStruck ? (
              <Eye className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5" aria-hidden />
            ) : (
              <EyeOff className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5" aria-hidden />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" className="font-ui text-xs">
          {strikeActionLabel}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
